import { LandingPageConfig } from '../types';

const API_BASE = process.env.NODE_ENV === 'production'
  ? '/api' // Production: Vercel routes /api/* to Express server
  : 'http://localhost:4000/api';

// Retry configuration
const MAX_RETRIES = 5;
const INITIAL_RETRY_DELAY = 1000; // 1 second
const MAX_RETRY_DELAY = 10000; // 10 seconds
const RETRY_MULTIPLIER = 2;

// Types for retry callbacks
export type RetryStatusCallback = (status: {
  isRetrying: boolean;
  attempt: number;
  maxAttempts: number;
  delay: number;
}) => void;

class ApiService {
  private retryStatusCallback: RetryStatusCallback | null = null;

  // Set callback for retry status updates
  setRetryStatusCallback(callback: RetryStatusCallback | null) {
    this.retryStatusCallback = callback;
  }

  private async sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private isRetryableError(error: any): boolean {
    // Retry on network errors, timeouts, or 5xx server errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      return true; // Network error
    }
    if (error instanceof Error) {
      const message = error.message.toLowerCase();
      if (message.includes('timeout') || 
          message.includes('buffering timed out') ||
          message.includes('network') ||
          message.includes('failed to fetch')) {
        return true;
      }
    }
    // Don't retry on 4xx client errors (except 408 timeout)
    if (error?.status >= 400 && error?.status < 500 && error?.status !== 408) {
      return false;
    }
    // Retry on 5xx server errors
    if (error?.status >= 500) {
      return true;
    }
    return false;
  }

  private async request<T>(endpoint: string, options?: RequestInit, retryCount = 0): Promise<T> {
    const url = `${API_BASE}${endpoint}`;

    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders(), // Add auth headers
          ...options?.headers,
        },
        ...options,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        (error as any).status = response.status;
        
        // Retry on 5xx errors
        if (response.status >= 500 && retryCount < MAX_RETRIES) {
          return this.retryRequest(endpoint, options, error, retryCount);
        }
        
        throw error;
      }

      // Success - notify callback that we're done retrying
      if (this.retryStatusCallback && retryCount > 0) {
        this.retryStatusCallback({ isRetrying: false, attempt: retryCount + 1, maxAttempts: MAX_RETRIES, delay: 0 });
      }

      return await response.json();
    } catch (error) {
      // Retry on network errors or timeouts
      if (this.isRetryableError(error) && retryCount < MAX_RETRIES) {
        return this.retryRequest(endpoint, options, error, retryCount);
      }

      console.error('API request failed:', error);
      throw error;
    }
  }

  private async retryRequest<T>(
    endpoint: string, 
    options: RequestInit | undefined, 
    error: any, 
    retryCount: number
  ): Promise<T> {
    const attempt = retryCount + 1;
    const delay = Math.min(
      INITIAL_RETRY_DELAY * Math.pow(RETRY_MULTIPLIER, retryCount),
      MAX_RETRY_DELAY
    );

    // Notify callback about retry
    if (this.retryStatusCallback) {
      this.retryStatusCallback({
        isRetrying: true,
        attempt,
        maxAttempts: MAX_RETRIES,
        delay: Math.round(delay / 1000) // Convert to seconds for display
      });
    }

    console.log(`Retrying request (attempt ${attempt}/${MAX_RETRIES}) after ${delay}ms...`);
    await this.sleep(delay);

    return this.request<T>(endpoint, options, attempt);
  }

  async saveLandingPage(config: LandingPageConfig): Promise<void> {
    // Check if this page already exists by ID (not slug)
    try {
      // First try to find the page by ID to see if it exists
      const existingPages = await this.loadAllLandingPages();
      const existingPage = existingPages.find(p => p.id === config.id);

      if (existingPage) {
        // Page exists, update it
        await this.request(`/pages/${config.id}`, {
          method: 'PUT',
          body: JSON.stringify(config),
        });
      } else {
        // Page doesn't exist, create it
        await this.request('/pages', {
          method: 'POST',
          body: JSON.stringify(config),
        });
      }
    } catch (error) {
      // If loading all pages fails, fall back to POST (create new)
      await this.request('/pages', {
        method: 'POST',
        body: JSON.stringify(config),
      });
    }
  }

  async loadLandingPage(slug: string): Promise<LandingPageConfig | null> {
    try {
      return await this.request<LandingPageConfig>(`/public/pages/${slug}`);
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  async loadLandingPageForEditing(slug: string): Promise<LandingPageConfig | null> {
    try {
      const page = await this.request<LandingPageConfig>(`/public/pages/${slug}`);
      if (page) {
        // Remove stats when loading for editing
        const { stats, ...pageWithoutStats } = page;
        return pageWithoutStats;
      }
      return null;
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  async loadAllLandingPages(): Promise<LandingPageConfig[]> {
    try {
      return await this.request<LandingPageConfig[]>('/pages');
    } catch (error) {
      console.error('Failed to load pages:', error);
      // Even after retries, return empty array to prevent UI blocking
      return [];
    }
  }

  async updateLandingPage(id: string, updates: Partial<LandingPageConfig>): Promise<LandingPageConfig> {
    return await this.request<LandingPageConfig>(`/pages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteLandingPage(id: string): Promise<void> {
    await this.request(`/pages/${id}`, {
      method: 'DELETE',
    });
  }

  async recordAnalytics(pageId: string, analyticsData: {
    visitorId: string;
    eventType: string;
    eventData?: any;
    userAgent?: string;
    ipAddress?: string;
  }): Promise<void> {
    await this.request(`/public/pages/${pageId}/analytics`, {
      method: 'POST',
      body: JSON.stringify(analyticsData),
    });
  }

  // Legacy method for backward compatibility
  async recordAnalyticsLegacy(pageId: string, type: string, data?: any): Promise<void> {
    await this.request(`/pages/${pageId}/stats`, {
      method: 'POST',
      body: JSON.stringify({ type, data }),
    });
  }

  // New stats endpoints
  async getRealtimeStats(pageId: string) {
    return await this.request(`/pages/${pageId}/stats/realtime`);
  }

  async getConversionFunnel(pageId: string) {
    return await this.request(`/pages/${pageId}/stats/funnel`);
  }

  /**
   * Calculate SHA-256 hash of a file for deduplication
   */
  private async calculateFileHash(file: File): Promise<string> {
    const arrayBuffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  }

  async uploadImage(file: File): Promise<string> {
    // Calculate hash of file for deduplication
    const fileHash = await this.calculateFileHash(file);
    const publicId = `landing-pages/${fileHash}`;

    // Request signature with public_id for deduplication
    const signResponse = await this.request<{ signature: string; timestamp: string; api_key: string; cloud_name: string; folder: string; public_id: string }>(
      `/upload/sign?public_id=${encodeURIComponent(publicId)}`
    );

    if (!signResponse) {
      throw new Error('Failed to get upload signature');
    }

    // Upload directly to Cloudinary with public_id and overwrite for deduplication
    const formData = new FormData();
    formData.append('file', file);
    formData.append('api_key', signResponse.api_key);
    formData.append('timestamp', signResponse.timestamp);
    formData.append('signature', signResponse.signature);
    formData.append('folder', signResponse.folder);
    formData.append('public_id', signResponse.public_id);
    formData.append('overwrite', 'true');

    const cloudinaryResponse = await fetch(`https://api.cloudinary.com/v1_1/${signResponse.cloud_name}/image/upload`, {
      method: 'POST',
      body: formData,
    });

    if (!cloudinaryResponse.ok) {
      throw new Error('Failed to upload to Cloudinary');
    }

    const result = await cloudinaryResponse.json();
    return result.secure_url;
  }

  // Authentication methods
  async login(password: string): Promise<{ token: string }> {
    return await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  }

  async logout(): Promise<void> {
    localStorage.removeItem('builder_token');
    return await this.request('/auth/logout', {
      method: 'POST',
    });
  }

  async verifyToken(): Promise<boolean> {
    try {
      const token = localStorage.getItem('builder_token');
      if (!token) return false;

      await this.request('/auth/verify', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      return true;
    } catch {
      localStorage.removeItem('builder_token');
      return false;
    }
  }

  private getAuthHeaders(): { [key: string]: string } {
    const token = localStorage.getItem('builder_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }
}

export const apiService = new ApiService();
